import React from 'react';

// Actions
import { deleteMFA, generateBackupCode, showModal } from '@/redux/modules/two-factor-auth';
import { getMe } from '@/redux/modules/auth';

// Components
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';
import ExternalLink from '@/views/components/ExternalLink';
import TwoFactorProtectedAction from '@/views/containers/auth/2fa/TwoFactorProtectedAction';
import { DestructiveButton, PrimaryButton } from '@/views/components/buttons';

// Components (Legacy)
import LegacySecurityTwoFactor from '@/views/containers/settings/LegacySecurityTwoFactor';
import { FormattedMessageV2 } from '@/views/components/i18n';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Libraries
import { connect } from 'react-redux';

// Types
import Locale from '@/types/locale';
import User from '@/types/user';
import { ReduxState } from '@/redux/modules/types';
import { TwoFactorEnableSteps } from '@/redux/modules/types/two-factor-auth';

// Utils
import notification from '@/utils/notification';
import { formatMessage } from '@/utils/i18n';

type DispatchProps = {
  deleteMFA: (otp: string) => Promise<void>;
  generateBackupCode: (otp: string) => Promise<void>;
  getMe: (contextId?: string) => Promise<User>;
  showModal: (startStep?: TwoFactorEnableSteps) => void;
};

type StateProps = {
  locale: Locale;
};

type Props = DispatchProps &
  StateProps & {
    user: User;
  };

const SecurityTwoFactorV2 = (props: Props) => {
  const {
    locale,
    user: {
      authUser: { mfa },
    },
    deleteMFA,
    generateBackupCode,
    getMe,
    showModal,
  } = props;

  // Variables
  const i18nBasePath = 'Settings.Security.TwoFactor';

  const disableTwoFactor = async (otp: string) => {
    await deleteMFA(otp);
    await getMe();
    notification.success(
      formatMessage('TwoFactorAuthentication.Notifications.Disabled.Description'),
      formatMessage('TwoFactorAuthentication.Notifications.Disabled.Title'),
    );
  };

  const renderEnable = () => {
    const primaryButton = {
      label: `${i18nBasePath}.Enable.EnableButton`,
      onClick: () => showModal(TwoFactorEnableSteps.BEGIN),
    };
    const secondaryActions = {
      links: [
        {
          isExternalLink: true,
          label: `${i18nBasePath}.LearnMore`,
          to: `${import.meta.env.REACT_APP_HELP_SITE_URL}/${locale.toLocaleLowerCase()}/articles/9315570206483`,
        },
      ],
    };

    return (
      <EmptyStateCard
        messagePath={`${i18nBasePath}.Enable.Description`}
        primaryButton={primaryButton}
        titlePath={`${i18nBasePath}.Enable.Title`}
        {...secondaryActions}
      />
    );
  };

  const renderDisable = () => (
    <>
      <FormattedMessageV2 className="two-factor-active-title" toLocalize={`${i18nBasePath}.Disable.Title`} />
      <FormattedMessageV2 className="two-factor-active-message" toLocalize={`${i18nBasePath}.Disable.Description`} />
      <div className="two-factor-active-actions">
        <TwoFactorProtectedAction action={generateBackupCode} description={formatMessage(`${i18nBasePath}.Disable.ProtectedActions.NewBackup`)}>
          {(openModal: () => void) => (
            <PrimaryButton className="primary-v2" onClick={openModal}>
              {formatMessage(`${i18nBasePath}.Disable.BackupCodeButton`)}
            </PrimaryButton>
          )}
        </TwoFactorProtectedAction>
        <div className="two-factor-active-secondary-actions">
          <ExternalLink className="external-link-v2" to={`${import.meta.env.REACT_APP_HELP_SITE_URL}/${locale.toLocaleLowerCase()}/articles/9315570206483`}>
            {formatMessage(`${i18nBasePath}.LearnMore`)}
          </ExternalLink>
          {!mfa?.required && (
            <TwoFactorProtectedAction action={disableTwoFactor} description={formatMessage(`${i18nBasePath}.Disable.ProtectedActions.DisableTwoFactor`)}>
              {(openModal: () => void) => (
                <DestructiveButton className="destructive-v2" onClick={openModal}>
                  {formatMessage(`${i18nBasePath}.Disable.DisableButton`)}
                </DestructiveButton>
              )}
            </TwoFactorProtectedAction>
          )}
        </div>
      </div>
    </>
  );

  return <div className="settings-security-two-factor">{mfa?.active ? renderDisable() : renderEnable()}</div>;
};

const SecurityTwoFactor: React.FC<Props> = props => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <SecurityTwoFactorV2 {...props} />;
  }

  return <LegacySecurityTwoFactor {...props} />;
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  locale: state.intl.locale,
});

export default connect(mapStateToProps, { deleteMFA, generateBackupCode, getMe, showModal })(SecurityTwoFactor);
